import React from 'react';
import { Link } from '@tanstack/react-router';
import { useAuth } from '../../contexts/AuthContext';
import { useBillingSummary } from '../../hooks/useBillingData';
import { billingMeters, formatDay, formatTrialRemaining } from '../../hooks/billingMeters';

const TRIAL_CONTACT_EMAIL = 'sushanth@trycanary.ai';

/** What the banner says, in priority order. Self-serve states need `user.canUseSelfServeBilling`. */
type BannerState =
  | { kind: 'lapsed'; plan: 'trial' | 'starter' | null }
  | { kind: 'past_due' }
  | { kind: 'meter'; label: string; level: 'warning' | 'full' }
  | { kind: 'trial'; trialEndsAt: string; selfServe: boolean };

function BillingLink({ children }: { children: React.ReactNode }) {
  return (
    <Link data-extend to="/settings/$section" params={{ section: 'billing' }}>
      {children}
    </Link>
  );
}

export function TrialBanner() {
  const { user, organizations } = useAuth();
  const currentOrg = organizations?.find((o) => o.id === user?.orgId);
  const selfServeOn = Boolean(user?.canUseSelfServeBilling);
  const isSelfServeOrg = currentOrg?.billingMode === 'self_serve';
  // Usage drives the meter and past_due states; the endpoint is knob-gated, so
  // it is only asked once the knob is on, and the banner works without it.
  const summary = useBillingSummary({ enabled: selfServeOn && isSelfServeOrg });
  const [dismissed, setDismissed] = React.useState(false);

  const trialEndsAt = currentOrg?.plan === 'trial' ? (currentOrg.trialEndsAt ?? null) : null;
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    if (!trialEndsAt) return;
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, [trialEndsAt]);

  // A contract org never sees a countdown or a self-serve state.
  if (!currentOrg || !isSelfServeOrg || dismissed) return null;

  const state = bannerState({
    plan: currentOrg.plan,
    access: currentOrg.access,
    trialEndsAt,
    selfServeOn,
    summary: summary.data,
  });
  if (!state) return null;

  return (
    <div data-component="trial-banner-cofactor">
      <span data-pulse />
      <BannerBody state={state} now={now} />
      <button data-dismiss type="button" aria-label="Dismiss" onClick={() => setDismissed(true)}>
        ×
      </button>
    </div>
  );
}

function bannerState(input: {
  plan: 'trial' | 'starter' | null;
  access: 'full' | 'lapsed';
  trialEndsAt: string | null;
  selfServeOn: boolean;
  summary: ReturnType<typeof useBillingSummary>['data'];
}): BannerState | null {
  const { plan, access, trialEndsAt, selfServeOn, summary } = input;
  if (selfServeOn) {
    if (access === 'lapsed') return { kind: 'lapsed', plan };
    if (summary?.subscription?.status === 'past_due') return { kind: 'past_due' };
    if (summary) {
      const meters = billingMeters(summary);
      const hit = [
        { label: 'Executions', meter: meters.executions },
        { label: 'Agent hours', meter: meters.agentHours },
      ]
        .filter((m) => m.meter.level !== 'ok')
        .sort(
          (a, b) =>
            Number(b.meter.level === 'full') - Number(a.meter.level === 'full') ||
            (b.meter.percent ?? 0) - (a.meter.percent ?? 0)
        )[0];
      if (hit && hit.meter.level !== 'ok') {
        return { kind: 'meter', label: hit.label, level: hit.meter.level };
      }
    }
  }
  return trialEndsAt ? { kind: 'trial', trialEndsAt, selfServe: selfServeOn } : null;
}

function BannerBody({ state, now }: { state: BannerState; now: Date }) {
  switch (state.kind) {
    case 'lapsed':
      return (
        <>
          <span>
            <strong>{state.plan === 'trial' ? 'Your trial has ended' : 'Your subscription is not active'}</strong>
          </span>
          <span style={{ color: 'rgba(255,255,255,.55)' }}>
            New runs and agent work are blocked until you subscribe.
          </span>
          <BillingLink>Subscribe →</BillingLink>
        </>
      );
    case 'past_due':
      return (
        <>
          <span>
            <strong>Your last payment failed</strong>
          </span>
          <span style={{ color: 'rgba(255,255,255,.55)' }}>
            Update your payment method to keep your plan.
          </span>
          <BillingLink>Manage billing →</BillingLink>
        </>
      );
    case 'meter':
      return (
        <>
          <span>
            <strong>{state.label}</strong> · {state.level === 'full' ? 'limit reached' : 'over 80% used'}
          </span>
          <BillingLink>View usage →</BillingLink>
        </>
      );
    case 'trial': {
      const endsAt = new Date(state.trialEndsAt);
      const { text, expired } = formatTrialRemaining(endsAt, now);
      return (
        <>
          {expired ? (
            <span>
              Trial · <strong>expired {text} ago</strong>
            </span>
          ) : (
            <span>
              Trial · <strong>{text} left</strong> · expires {formatDay(state.trialEndsAt)}
            </span>
          )}
          {state.selfServe ? (
            <BillingLink>Subscribe →</BillingLink>
          ) : (
            <>
              <span style={{ color: 'rgba(255,255,255,.55)' }}>
                Reach out at <a href={`mailto:${TRIAL_CONTACT_EMAIL}`}>{TRIAL_CONTACT_EMAIL}</a> to{' '}
                {expired ? 'continue' : 'extend'}.
              </span>
              <a data-extend href={`mailto:${TRIAL_CONTACT_EMAIL}`} role="button">
                Talk to sales →
              </a>
            </>
          )}
        </>
      );
    }
  }
}
